
/**
 * Prepends a zero in front of a number, if the number is less than 10.
 *
 * Args:
 *      n (Number): The number to prepend a zero to.
 *
 * Returns:
 *      (String): The prepended number value.
 *
 */
export function appendLeadingZeroes(n) {
    if (n <= 9) {
        return `0${ n }`;
    }
    return n;
}

/**
 * Converts a date to the local timezone.
 *
 * Args:
 *      dateString (String): date value as a string.
 *
 * Returns:
 *      (String): Converted date.
 *
 */
export function toLocalDate(dateString) {
    /** comment out because seattle is using PT only now
     * let date = new Date(dateString);
     * date = new Date((date.getTime() + date.getTimezoneOffset() * 60000));
     * return date;
     */
    return new Date(dateString);
}

/**
 * Converts a date to the UTC format.
 *
 * Args:
 *      dateString (String): date value as a string.
 *
 * Returns:
 *      (String): Converted date on browser local time zone
 *
 */
export function toUTCFormat(dateString) {
    const cdt = new Date(dateString);
    const toUTC = Date.UTC(cdt.getFullYear(), cdt.getMonth() + 1, cdt.getDate(), cdt.getHours(), cdt.getMinutes(), cdt.getSeconds(), cdt.getMilliseconds() );
    return new Date(toUTC); // Javascript will convert to local time zone;
}

/**
 * Get target offset to convert date to UCT to PST time zone
 *
 * Args:
 *      date (Date): date as Date
 *
 * Returns:
 *      (String): the target offset
 *
 */

 export function targetOffsetConvertUTCToPST(date) {
    const toUTC = Date.UTC(date.getFullYear(), date.getMonth() + 1, date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds() );
   let offsetTarget = -7;
    if ( toUTC < Date.UTC(2021, 11, 7, 2, 0)) {
        offsetTarget = -7;
    } else if (toUTC < Date.UTC(2022, 3, 13, 2, 0)) {
        offsetTarget = -8;
    } else if ( toUTC < Date.UTC(2022, 11, 6, 2, 0)) {
        offsetTarget = -7;
    } else if (toUTC < Date.UTC(2023, 3, 12, 2, 0)) {
        offsetTarget = -8;
    } else if (toUTC < Date.UTC(2023, 11, 5, 2, 0)) {
        offsetTarget = -7;
    } else if (toUTC < Date.UTC(2024, 3, 10, 2, 0)) {
        offsetTarget = -8;
    } else if (toUTC < Date.UTC(2024, 11, 3, 2, 0)) {
        offsetTarget = -7;
    } else if (toUTC < Date.UTC(2025, 3, 9, 2, 0)) {
        offsetTarget = -8;
    } else if (toUTC < Date.UTC(2025, 11, 2, 2, 0)) {
        offsetTarget = -7;
    } else if (toUTC < Date.UTC(2026, 3, 8, 2, 0)) {
        offsetTarget = -8;
    } else if (toUTC < Date.UTC(2026, 11, 1, 2, 0)) {
        offsetTarget = -7;
    } else if (toUTC < Date.UTC(2027, 3, 14, 2, 0)) {
        offsetTarget = -8;
    } else if (toUTC < Date.UTC(2027, 11, 7, 2, 0)) {
        offsetTarget = -7;
    } else if (toUTC < Date.UTC(2028, 3, 12, 2, 0)) {
        offsetTarget = -8;
    } else if (toUTC < Date.UTC(2028, 11, 5, 2, 0)) {
        offsetTarget = -7;
    } else if (toUTC < Date.UTC(2029, 3, 12, 2, 0)) {
        offsetTarget = -8;
    } else if (toUTC < Date.UTC(2029, 11, 4, 2, 0)) {
        offsetTarget = -7;
    }
   return offsetTarget;
 }

/**
 * Converts a date to the Pacific Standard Time format.
 *
 * Args:
 *      dateString (String): date value as a string. It's UTC Time Zone
 *
 * Returns:
 *      (String): Converted date on Pacific Standard Time
 *
 */

export function seattleLocalTime(dateString) {
    if (dateString) {
        try {
            // dateString - It's already UTC time Zone
            let date = new Date(dateString);
            if (dateString.indexOf('T') > -1 || dateString.indexOf('Z') > -1) {
                const jsDateTime = `${ dateString.substring(0, 4) }/${ dateString.substring(4, 6) }/${ dateString.substring(6, 8) } ${ dateString.substring(9, 11) }:${ dateString.substring(11, 13) }:${ dateString.substring(13, 15) }`;
                date = new Date(jsDateTime);
            }
            // Get offset target to convert UTC to PST.
            const targetOffset = targetOffsetConvertUTCToPST(date);

            // Convert UTC to PST time
            const pstTime = date.getTime() + (3600000 * targetOffset);

            // Get PST Time and Format to formatDate -20210629T113000Z
            const pstDate = new Date(pstTime);
            return `${pstDate.getFullYear()}${appendLeadingZeroes(pstDate.getMonth() + 1)}${appendLeadingZeroes(pstDate.getDate())}T${appendLeadingZeroes(pstDate.getHours())}${appendLeadingZeroes(pstDate.getMinutes())}00Z`;
        } catch (ex) {
            // error
        }
    }
}

export function formatDate(dateString, format, utc = false) {
     if (dateString) {
        try {
            let date = new Date(dateString);
            let month = date.getMonth() + 1;
            const hours = date.getHours();
            const isPM = hours >= 12;
            const isMidday = hours === 12;
            let parsedHours = hours - (isPM && !isMidday ? 12 : 0);

            if (dateString.indexOf('T') > -1 || dateString.indexOf('Z') > -1) {
                const jsDateTime = `${ dateString.substring(0, 4) }-${ dateString.substring(4, 6) }-${ dateString.substring(6, 8) } ${ dateString.substring(9, 11) }:${ dateString.substring(11, 13) }:${ dateString.substring(13, 15) }`;
                date = new Date(jsDateTime);
                month = date.getMonth();
                parsedHours = date.getHours();
            }

            if (!format) {
                return `${ date.toString() }`;
            }

            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

            let formattedDate = format.replace('MMMM', monthNames[date.getMonth()]);
            formattedDate = formattedDate.replace('dddd', dayNames[date.getDay()]);
            formattedDate = formattedDate.replace('MM', appendLeadingZeroes(month));
            formattedDate = formattedDate.replace('mm', appendLeadingZeroes(date.getMinutes()));
            formattedDate = formattedDate.replace('HH', parsedHours);
            formattedDate = formattedDate.replace('ss', appendLeadingZeroes(date.getSeconds()));
            formattedDate = formattedDate.replace('yyyy', date.getFullYear());
            if (utc) {
                formattedDate = formattedDate.replace('dd', appendLeadingZeroes(date.getUTCDate()));
            } else {
                formattedDate = formattedDate.replace('dd', appendLeadingZeroes(date.getDate()));
            }

            if (format.indexOf('HH') > -1 || format.indexOf('H') > -1) {
                formattedDate += (isPM ? ' PM' : ' AM');
            }

            return formattedDate;
        } catch (ex) {
            // error
        }
    }

    return `${ dateString }`;
}
